import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Upload, FileSpreadsheet, Download } from 'lucide-react';
import { getCuttingTemplateUrl } from '../../services/api';

export function CuttingUploadCard(props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload size={20} />Upload Cutting List
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FileDropzone 
          cuttingFile={props.cuttingFile}
          handleFileChange={props.handleFileChange}
          clearFile={props.clearFile}
        />
        <SettingsFields 
          cuttingSettings={props.cuttingSettings}
          updateBlade={props.updateBlade}
          updateTrim={props.updateTrim}
          updateJob={props.updateJob}
          jobs={props.jobs}
        />
        <Button 
          className="w-full btn-accent" 
          onClick={props.handleCuttingUpload} 
          disabled={!props.cuttingFile || props.cuttingProcessing} 
          data-testid="process-cutting-btn"
        >
          {props.cuttingProcessing ? 'Processing...' : 'Process Cutting List'}
        </Button>
      </CardContent>
    </Card>
  );
}

function FileDropzone({ cuttingFile, handleFileChange, clearFile }) {
  return (
    <div>
      <div className="border-2 border-dashed rounded-lg p-6 text-center">
        <FileSpreadsheet size={40} className="mx-auto mb-3 text-muted-foreground" />
        <Input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} className="max-w-xs mx-auto" data-testid="cutting-file-input" />
        <p className="text-sm text-muted-foreground mt-2">CSV or Excel: item_code, required_length_mm, quantity</p>
        <a href={getCuttingTemplateUrl()} download className="text-sm text-blue-600 hover:underline flex items-center justify-center gap-1 mt-2">
          <Download size={14} />Download Template
        </a>
      </div>
      {cuttingFile && (
        <div className="bg-muted/50 p-3 rounded flex items-center justify-between mt-4">
          <span className="text-sm font-medium">{cuttingFile.name}</span>
          <Button variant="ghost" size="sm" onClick={clearFile}>Remove</Button>
        </div>
      )}
    </div>
  );
}

function SettingsFields({ cuttingSettings, updateBlade, updateTrim, updateJob, jobs }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Blade Kerf (mm)</Label>
          <Input type="number" step="0.5" value={cuttingSettings.blade_kerf_mm} onChange={updateBlade} className="mt-1" />
        </div>
        <div>
          <Label>End Trim (mm)</Label>
          <Input type="number" step="0.5" value={cuttingSettings.end_trim_mm} onChange={updateTrim} className="mt-1" />
        </div>
      </div>
      <div>
        <Label>Associated Job (Optional)</Label>
        <Select value={cuttingSettings.job_id} onValueChange={updateJob}>
          <SelectTrigger className="mt-1"><SelectValue placeholder="Select job" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No Job</SelectItem>
            {jobs.map(function(j) { return <SelectItem key={j.id} value={j.id}>{j.job_number}</SelectItem>; })}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function FormulaCard() {
  return (
    <Card>
      <CardHeader><CardTitle>Wastage Formula</CardTitle></CardHeader>
      <CardContent>
        <div className="bg-slate-50 p-4 rounded font-mono text-sm">
          <p className="mb-2 font-bold">Total Consumption =</p>
          <p className="ml-4">(Length x Qty) + Kerf + Trim</p>
        </div>
        <div className="mt-4 p-3 bg-orange-50 rounded border border-orange-200">
          <p className="text-sm font-medium text-orange-800">Remnant Threshold: 1500mm</p>
          <p className="text-sm text-orange-700">Off-cuts over 1500mm saved to Remnant Registry</p>
        </div>
      </CardContent>
    </Card>
  );
}
